import { useCallback, useMemo } from "react";
import useSWRInfinite from "swr/infinite";
import { listTasksPage, type Task, type TaskPage } from "@/services/task.service";

const PAGE_SIZE = 200;

export interface TaskFeedFilters {
	project_id?: string;
	sprint_id?: string;
}

type FeedKey = ["tasks", string, string, number];

/**
 * Paginated all-tasks feed for the Tasks page. Project/sprint filter on the
 * server; pages load on demand via `loadMore`. Pass `null` to hold the fetch.
 */
export function useTaskFeed(filters: TaskFeedFilters | null) {
	const { data, error, size, setSize, isValidating, mutate } = useSWRInfinite<
		TaskPage,
		Error
	>(
		(index, prev: TaskPage | null): FeedKey | null => {
			if (!filters) return null;
			if (prev && prev.data.length < PAGE_SIZE) return null;
			return [
				"tasks",
				filters.project_id ?? "",
				filters.sprint_id ?? "",
				index + 1,
			];
		},
		([, project_id, sprint_id, page]: FeedKey) =>
			listTasksPage({
				project_id: project_id || undefined,
				sprint_id: sprint_id || undefined,
				page,
				limit: PAGE_SIZE,
			}),
		{ keepPreviousData: true },
	);

	// ponytail: offset paging, so a task created/deleted between page loads
	// shifts rows (dupes dropped here, a skipped row shows on next refresh).
	// Switch to a created_at/id cursor if that matters.
	const tasks = useMemo(() => {
		const seen = new Set<string>();
		return (data ?? [])
			.flatMap((p) => p.data)
			.filter((t) => {
				if (seen.has(t.id)) return false;
				seen.add(t.id);
				return true;
			});
	}, [data]);
	const total = data?.[0]?.total ?? 0;
	const lastPage = data?.[data.length - 1];
	const fetched = data?.reduce((n, p) => n + p.data.length, 0) ?? 0;

	// Local edit applied to every loaded page without refetching; untouched
	// tasks keep their object refs.
	const patchTasks = useCallback(
		(fn: (tasks: Task[]) => Task[]) =>
			mutate((pages) => pages?.map((p) => ({ ...p, data: fn(p.data) })), {
				revalidate: false,
			}),
		[mutate],
	);

	const loadMore = useCallback(() => setSize((s) => s + 1), [setSize]);
	// Refetches every loaded page.
	const refresh = useCallback(() => mutate(), [mutate]);

	return {
		tasks,
		total,
		error,
		isLoading: !data && !error && !!filters,
		isValidating,
		isLoadingMore: !!data && size > data.length,
		hasMore: lastPage?.data.length === PAGE_SIZE && fetched < total,
		loadMore,
		patchTasks,
		refresh,
	};
}
